import * as Phaser from 'phaser';
import { PALETTE } from '../render/palette';
import type { PrismInputDir } from './prismDirections';
import { OPPOSITE, CCW_SCREEN, CW_SCREEN } from './prismDirections';

/**
 * Faint colored arrows from each prism output face. Visible when showPrismHelper
 * is true on the level; faded out by the scene after a short delay or rotation.
 */
export class PrismHelperOverlay extends Phaser.GameObjects.Container {
  private readonly arrows: Record<'R' | 'G' | 'B', Phaser.GameObjects.Triangle>;
  private fadeTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, cellSize: number) {
    super(scene);
    const armLen = cellSize * 0.4;
    this.arrows = {
      R: scene.add.triangle(0, 0, 0, -6, 0, 6, armLen, 0, PALETTE.R).setOrigin(0.5).setAlpha(0.6),
      G: scene.add.triangle(0, 0, 0, -6, 0, 6, armLen, 0, PALETTE.G).setOrigin(0.5).setAlpha(0.6),
      B: scene.add.triangle(0, 0, 0, -6, 0, 6, armLen, 0, PALETTE.B).setOrigin(0.5).setAlpha(0.6),
    };
    this.add([this.arrows.R, this.arrows.G, this.arrows.B]);
    scene.add.existing(this);
  }

  setInputDir(inputDir: PrismInputDir, cellSize: number): void {
    const offsets: Record<PrismInputDir, { x: number; y: number; rot: number }> = {
      N: { x: 0, y: -cellSize * 0.4, rot: -Math.PI / 2 },
      S: { x: 0, y: cellSize * 0.4, rot: Math.PI / 2 },
      E: { x: cellSize * 0.4, y: 0, rot: 0 },
      W: { x: -cellSize * 0.4, y: 0, rot: Math.PI },
    };
    const place = (a: Phaser.GameObjects.Triangle, dir: PrismInputDir): void => {
      const o = offsets[dir];
      a.setPosition(o.x, o.y);
      a.setRotation(o.rot);
    };
    place(this.arrows.G, OPPOSITE[inputDir]);
    place(this.arrows.R, CCW_SCREEN[inputDir]);
    place(this.arrows.B, CW_SCREEN[inputDir]);
  }

  fadeOut(durationMs = 2500): void {
    this.fadeTween?.stop();
    this.fadeTween = this.scene.tweens.add({
      targets: [this.arrows.R, this.arrows.G, this.arrows.B],
      alpha: 0,
      duration: durationMs,
      ease: 'Sine.easeOut',
    });
  }
}
